"use client";

import { useMemo, useState } from "react";
import { Field, Stat, fmtMoney } from "../ToolUi";

export default function CalculatorClient() {
  const [monthlyCustomers, setMonthlyCustomers] = useState("800");
  const [shareRate, setShareRate] = useState("10");
  const [conversionRate, setConversionRate] = useState("20");
  const [aov, setAov] = useState("60");
  const [rewardCost, setRewardCost] = useState("15");

  const results = useMemo(() => {
    const customers = Math.max(0, parseFloat(monthlyCustomers) || 0);
    const share = Math.min(100, Math.max(0, parseFloat(shareRate) || 0)) / 100;
    const convert =
      Math.min(100, Math.max(0, parseFloat(conversionRate) || 0)) / 100;
    const avg = Math.max(0, parseFloat(aov) || 0);
    const cost = Math.max(0, parseFloat(rewardCost) || 0);

    const sharers = customers * share;
    const referredCustomers = sharers * convert;
    const referralRevenue = referredCustomers * avg;
    const totalRewardCost = referredCustomers * cost;
    const cac = referredCustomers > 0 ? cost : null;
    const revenuePerDollar =
      totalRewardCost > 0 ? referralRevenue / totalRewardCost : null;

    return {
      referredCustomers,
      referralRevenue,
      yearlyRevenue: referralRevenue * 12,
      totalRewardCost,
      cac,
      revenuePerDollar,
    };
  }, [monthlyCustomers, shareRate, conversionRate, aov, rewardCost]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Inputs */}
      <div className="flex flex-col gap-5 bg-[#1E1E1E] border-2 border-white/10 rounded-2xl p-6 md:p-8">
        <h2 className="font-normal text-[24px] text-[#f8f7f4] tracking-[-0.5px]">
          Your referral assumptions
        </h2>
        <Field
          label="Customers per month"
          value={monthlyCustomers}
          onChange={setMonthlyCustomers}
        />
        <Field
          label="Customers who share their referral link"
          suffix="%"
          value={shareRate}
          onChange={setShareRate}
          max={100}
        />
        <Field
          label="Shared links that convert to a first order"
          suffix="%"
          value={conversionRate}
          onChange={setConversionRate}
          max={100}
        />
        <Field
          label="Average order value"
          suffix="USD"
          value={aov}
          onChange={setAov}
          step="0.01"
        />
        <Field
          label="Total reward cost per successful referral (both sides)"
          suffix="USD"
          value={rewardCost}
          onChange={setRewardCost}
          step="0.01"
        />
        <p className="text-[13px] text-[#909090] leading-5">
          Typical Shopify benchmarks: 5-15% of customers share when asked at
          the right moment, and 15-30% of shared links convert.
        </p>
      </div>

      {/* Results */}
      <div className="flex flex-col gap-4">
        <Stat
          label="Referral revenue per month"
          value={fmtMoney(results.referralRevenue)}
          highlight
        />
        <Stat
          label="Referral revenue per year"
          value={fmtMoney(results.yearlyRevenue)}
        />
        <div className="grid grid-cols-2 gap-4">
          <Stat
            label="New customers per month"
            value={Math.round(results.referredCustomers).toLocaleString("en-US")}
          />
          <Stat
            label="Cost per acquired customer"
            value={results.cac === null ? "—" : fmtMoney(results.cac)}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Stat
            label="Monthly reward cost"
            value={fmtMoney(results.totalRewardCost)}
          />
          <Stat
            label="Revenue per $1 of rewards"
            value={
              results.revenuePerDollar === null
                ? "—"
                : fmtMoney(results.revenuePerDollar)
            }
          />
        </div>

        <a
          href="https://apps.shopify.com/keystone-loyalty-rewards?utm_source=website&utm_medium=organic&utm_campaign=referral-planner"
          className="mt-2 inline-flex items-center justify-center h-[52px] bg-[#dbdacd] border border-[#f8f7f4] rounded-[10px] px-6 text-base font-medium text-[#3e3e3e] hover:bg-[#cccbbe] transition-colors"
        >
          Launch referrals with Keystone Loyalty
        </a>
      </div>
    </div>
  );
}
